// Shopify related types

// Define action type constants to avoid string literals
export const ACTION_TYPES = {
  DISPLAY_ITEMS: 'DISPLAY_ITEMS',
  GET_MORE_ITEMS: 'GET_MORE_ITEMS',
  SORT_ITEMS: 'SORT_ITEMS',
  FILTER_ITEMS: 'FILTER_ITEMS',
  REMOVE_FILTER: 'REMOVE_FILTER',
  TOGGLE_UNAVAILABLE_PRODUCTS: 'TOGGLE_UNAVAILABLE_PRODUCTS',
  TOGGLE_HALF_THE_PRICE_PRODUCTS: 'TOGGLE_HALF_THE_PRICE_PRODUCTS',
  SAVE_ORIGINAL_ITEMS: 'SAVE_ORIGINAL_ITEMS', // New action to save original items for restoration
  GET_MATCHED_ITEMS: 'GET_MATCHED_ITEMS',
  SET_LOADING_STATE: 'SET_LOADING_STATE',
  SET_LOADING_MORE_STATE: 'SET_LOADING_MORE_STATE',
  SET_ERROR_STATE: 'SET_ERROR_STATE',
  SET_PAGINATION_INFO: 'SET_PAGINATION_INFO', // Update pagination info for proper cursor management
  SET_EMPTY_HALF_PRICE_BATCH_COUNT: 'SET_EMPTY_HALF_PRICE_BATCH_COUNT' // Track empty half-price batches
} as const;

// Create a union type of all action types
export type ActionType = typeof ACTION_TYPES[keyof typeof ACTION_TYPES];

// Define the sort types
export type SortType = '' | 'A-Z' | 'Z-A' | 'HighestPrice' | 'LowestPrice' | 'Difference';
export type SortTypeName = '' | 'Name (A-Z)' | 'Name (Z-A)' | 'Price (High to Low)' | 'Price (Low to High)' | 'Category (A-Z)' | 'Category (Z-A)' | 'Biggest Savings';

export interface PaginationInfo {
  hasNextPage: boolean;
  endCursor: string | null;
  collectionHandle: string | null;
  searchTerm: string | null;
}

// Define Shopify-specific interfaces
export interface ShopifyMetafield {
  namespace: string;
  key: string;
  value: string;
}

// Extend ItemPricings to include was_price
declare global {
  interface ItemPricings {
    was_price?: number;
    multibuy_quantity?: number;
    multibuy_price?: number;
    multibuy_description?: string;
  }
}

// Create a type for the extended state
export interface ShopifyItemsState {
  paginationInfo: PaginationInfo;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  allItems?: ItemData[]; // Store all unfiltered items for restoration when filters are removed
  originalItemsBeforeHalfPrice?: ItemData[]; // Store the original items before half-price filtering is applied
  emptyHalfPriceBatchCount: number; // Count consecutive empty batches when filtering for half-price items
}

// Create a type for the extended action
export interface ShopifyItemAction {
  type: ActionType;
  paginationInfo?: PaginationInfo;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  error?: string | null;
  originalItems?: ItemData[]; // For saving original items before filtering
  emptyHalfPriceBatchCount?: number; // For setting empty half-price batch count
  count?: number; // For updating empty batch count
}

// Add these to the global ItemsState and ItemAction
declare global {
  interface ItemsState extends ShopifyItemsState {}
  interface ItemAction extends ShopifyItemAction {}
  
  // Add metafields to ItemData
  interface ItemData {
    metafields?: ShopifyMetafield[];
    shopify_id?: string;
    quantity?: number;
  }
}

// Define Shopify page interfaces
export interface Page {
  id: string;
  title: string;
  handle: string;
  summary: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  url?: string;
}

export interface ShopifyBlog {
  id: string;
  handle: string;
  title: string;
  url?: string;
}

export interface ShopifyArticleImage {
  url: string;
  altText?: string;
}

export interface ShopifyArticleAuthor {
  name: string;
}

export interface ShopifyArticle {
  id: string;
  handle: string;
  title: string;
  excerpt?: string;
  content: string;
  contentHtml: string;
  publishedAt: string;
  image?: ShopifyArticleImage;
  author?: ShopifyArticleAuthor;
  tags?: string[];
  blogHandle?: string;
}

export interface ShopifyArticlesResponse {
  articles: ShopifyArticle[];
  pageInfo: ShopifyPageInfo;
}

export interface Recipe {
  id: string;
  title: string;
  handle: string;
  excerpt?: string;
  content: string;
  contentHtml: string;
  publishedAt: string;
  image?: {
    url: string;
    altText?: string;
  };
  author?: {
    name: string;
  };
  tags: string[];
  blogHandle?: string;
}

export interface ShopifyPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ShopifyPage {
  id: string;
  handle: string;
  title: string;
  body: string;
  bodySummary?: string;
  createdAt: string;
  updatedAt: string;
  url?: string;
}

export interface ShopifyPagesResponse {
  pages: ShopifyPage[];
  pageInfo: ShopifyPageInfo;
}

export {};
